import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { type ComponentProps, type ReactElement, type ReactNode } from 'react';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { ChartEmptyState } from '@/ui/composites/charts/ChartEmptyState';
import { DropdownFiltersPopover } from '@/ui/composites/filters/DropdownFiltersPopover';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { Panel } from '@/ui/composites/Panel';
import { Box, Cancel, IconButton, Text, Tooltip, useTheme } from '@/ui/primitives';
import { useMediaQuery } from '@/ui/primitives/hooks';
import { TABLE_PANEL_HEADER_HEIGHT } from '../tableSystem/tableConstants';
import { TableDatePopover } from '../tableSystem/TableDatePopover';
import {
  buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
} from '../tableSystem/tableToolbar.helpers';
import {
  type TableDropdownOption,
  type TableStatusOption,
} from '../tableSystem/tableToolbar.types';
import { useTableToolbarState } from '../tableSystem/useTableToolbarState';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { Dayjs } from 'dayjs';

const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: 'subtitle1.fontSize',
      lineHeight: 1.4,
    },
  },
} as const;

const chartToolbarColumnVisibilityModel = {} satisfies GridColumnVisibilityModel;
const chartToolbarColumnOptions: Array<{ field: string; mandatory?: boolean }> = [];
const chartToolbarSortKey = 'default' as const;

type ChartPanelToolbarProps<TStatus extends string> = {
  title: string;
  headerRightSlot?: ReactNode;
  showToolbarControls?: boolean;
  headerHeight?: number;
  headerTitlePaddingLeft?: string | number;
  headerControlsPaddingRight?: string | number;
  searchAriaLabel: string;
  searchValue?: string;
  onDebouncedSearchChange: (value: string) => void;
  filterStatuses: TStatus[];
  statusOptions: Array<TableStatusOption<TStatus>>;
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities?: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  entityOptions?: TableDropdownOption[];
  entityLabel?: string;
  filterSuites?: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  suiteOptions?: TableDropdownOption[];
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  dateQuickFilter: CloneDateQuickFilter;
  onDateQuickFilterChange: (next: CloneDateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  availableTags: string[];
  includeLatestQuickFilter?: boolean;
  statusFilterLabel?: string;
};

const ChartPanelToolbar = function ChartPanelToolbar<TStatus extends string>({
  title,
  headerRightSlot,
  showToolbarControls = true,
  headerHeight = TABLE_PANEL_HEADER_HEIGHT,
  headerTitlePaddingLeft = s('sm'),
  headerControlsPaddingRight = s('sm'),
  searchAriaLabel,
  searchValue: externalSearchValue,
  onDebouncedSearchChange,
  filterStatuses,
  statusOptions,
  onFilterStatusesChange,
  filterEntities = [],
  onFilterEntitiesChange,
  entityOptions,
  entityLabel,
  filterSuites = [],
  onFilterSuitesChange,
  suiteOptions,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  availableTags,
  includeLatestQuickFilter = false,
  statusFilterLabel = 'Status',
}: ChartPanelToolbarProps<TStatus>): ReactElement {
  const theme = useTheme();
  const isCompactHeaderLayout =
    !showToolbarControls &&
    headerRightSlot !== undefined &&
    useMediaQuery(theme.breakpoints.down('lg'));
  const toolbarControlSize = 32;
  const toolbarExpandedSearchWidth = 252;
  const toolbarSearchWidthPx = `${toolbarExpandedSearchWidth}px`;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const {
    popoverState,
    searchExpanded,
    searchValue,
    hoverTarget,
    searchInputRef,
    hasActiveFilters,
    hasActiveDateFilters,
    hasActiveToolbarState,
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    setHoverTarget,
    setSearchValue,
    openSearch,
    handleSearchBlur,
    handleSearchKeyDown,
    openPopover,
    closePopover,
    preserveWindowScroll,
    resetToolbarFilters,
  } = useTableToolbarState<typeof chartToolbarSortKey, TStatus>({
    searchValue: externalSearchValue,
    onDebouncedSearchChange,
    columnVisibilityModel: chartToolbarColumnVisibilityModel,
    defaultColumnVisibilityModel: chartToolbarColumnVisibilityModel,
    columnOptions: chartToolbarColumnOptions,
    onColumnVisibilityModelChange: () => undefined,
    sortKey: chartToolbarSortKey,
    sortDirection: 'desc',
    defaultSortKey: chartToolbarSortKey,
    defaultSortDirection: 'desc',
    onSortChange: () => undefined,
    filterStatuses,
    onFilterStatusesChange,
    filterEntities,
    onFilterEntitiesChange,
    filterSuites,
    onFilterSuitesChange,
    filterTags,
    onFilterTagsChange,
    dateQuickFilter,
    onDateQuickFilterChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
  });
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const toolbarIconCircleSx = buildToolbarIconCircleSx;
  const toolbarIconTooltipDelayMs = 250;
  const controlsOffsetRight =
    typeof headerControlsPaddingRight === 'number'
      ? `${headerControlsPaddingRight * -1}px`
      : `calc(${headerControlsPaddingRight} * -1)`;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap="md"
        sx={{
          width: '100%',
          minHeight: `${headerHeight}px`,
          height: `${headerHeight}px`,
        }}
      >
        <Box
          sx={{
            minHeight: `${toolbarControlSize}px`,
            display: 'flex',
            alignItems: 'center',
            pl: headerTitlePaddingLeft,
            minWidth: 0,
            flex: isCompactHeaderLayout ? '0 0 auto' : '1 1 auto',
          }}
        >
          <Text
            role="label"
            sx={{
              color: 'text.secondary',
              fontSize: 'subtitle1.fontSize',
              lineHeight: 1.4,
            }}
          >
            {title}
          </Text>
        </Box>
        {showToolbarControls ? (
          <Stack
            direction="row"
            alignItems="center"
            gap="sm"
            sx={{
              minWidth: 0,
              height: `${headerHeight}px`,
              minHeight: `${headerHeight}px`,
              boxSizing: 'border-box',
              justifyContent: 'center',
              mr: controlsOffsetRight,
            }}
          >
            {dateRangeLabel ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Text
                  role="bodyMuted"
                  sx={{
                    fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                    lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
                    fontWeight: (muiTheme) => muiTheme.typography.fontWeightMedium,
                  }}
                >
                  {dateRangeLabel}
                </Text>
              </Box>
            ) : null}
            <Tooltip
              title="Date Filters"
              arrow
              enterDelay={toolbarIconTooltipDelayMs}
              slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            >
              <IconButton
                size="medium"
                aria-label="Open chart date filters"
                onClick={openPopover('date')}
                onMouseEnter={() => {
                  setHoverTarget('date');
                }}
                onMouseLeave={() => {
                  setHoverTarget(null);
                }}
                sx={[
                  toolbarIconButtonSx,
                  { color: hasActiveDateFilters ? 'primary.main' : toolbarInactiveIconColor },
                ]}
              >
                <Box sx={toolbarIconCircleSx(hoverTarget === 'date')}>
                  <EventOutlinedIcon
                    sx={{
                      color: hasActiveDateFilters ? 'primary.main' : toolbarInactiveIconColor,
                      width: '20px !important',
                      height: '20px !important',
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Filters"
              arrow
              enterDelay={toolbarIconTooltipDelayMs}
              slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            >
              <IconButton
                size="medium"
                aria-label="Open chart filters"
                onClick={openPopover('filters')}
                onMouseEnter={() => {
                  setHoverTarget('filters');
                }}
                onMouseLeave={() => {
                  setHoverTarget(null);
                }}
                sx={[
                  toolbarIconButtonSx,
                  { color: hasActiveFilters ? 'primary.main' : toolbarInactiveIconColor },
                ]}
              >
                <Box sx={toolbarIconCircleSx(hoverTarget === 'filters')}>
                  <FilterListOutlinedIcon
                    sx={{
                      color: hasActiveFilters ? 'primary.main' : toolbarInactiveIconColor,
                      width: '22px !important',
                      height: '22px !important',
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                width: searchExpanded ? toolbarExpandedSearchWidth : 32,
                height: toolbarControlSize,
                minHeight: toolbarControlSize,
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: (t) =>
                  t.transitions.create('width', {
                    duration: t.transitions.duration.shorter,
                    easing: t.transitions.easing.easeInOut,
                  }),
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  width: '100%',
                  height: `${toolbarControlSize}px`,
                  transform: searchExpanded
                    ? 'translateY(-50%) translateX(0)'
                    : 'translateY(-50%) translateX(-8px)',
                  opacity: searchExpanded ? 1 : 0,
                  pointerEvents: searchExpanded ? 'auto' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  transition: (t) =>
                    t.transitions.create(['opacity', 'transform'], {
                      duration: t.transitions.duration.shorter,
                      easing: t.transitions.easing.easeInOut,
                    }),
                }}
              >
                <SearchFilterField
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                  }}
                  placeholder="Search..."
                  inputAriaLabel={searchAriaLabel}
                  controlHeight={32}
                  compact
                  leadingSearchAdornment
                  trailingSearchAdornment={false}
                  filledClearIcon={false}
                  sx={{
                    width: toolbarSearchWidthPx,
                    minWidth: toolbarSearchWidthPx,
                    maxWidth: toolbarSearchWidthPx,
                    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                      height: `${toolbarControlSize}px !important`,
                    },
                    '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
                      height: `${toolbarControlSize}px !important`,
                      paddingLeft: `${s('xs')} !important`,
                      paddingInlineStart: `${s('xs')} !important`,
                    },
                  }}
                  slotProps={{
                    htmlInput: {
                      ref: (instance: HTMLInputElement | null) => {
                        searchInputRef.current = instance;
                      },
                      onBlur: handleSearchBlur,
                      onKeyDown: handleSearchKeyDown,
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  opacity: searchExpanded ? 0 : 1,
                  pointerEvents: searchExpanded ? 'none' : 'auto',
                  transform: searchExpanded ? 'scale(0.9)' : 'scale(1)',
                  transition: (t) =>
                    t.transitions.create(['opacity', 'transform'], {
                      duration: t.transitions.duration.shorter,
                      easing: t.transitions.easing.easeInOut,
                    }),
                }}
              >
                <Tooltip
                  title="Search"
                  arrow
                  enterDelay={toolbarIconTooltipDelayMs}
                  slotProps={CLONE_TOOLTIP_SLOT_PROPS}
                >
                  <IconButton
                    size="medium"
                    aria-label="Open chart search"
                    onClick={openSearch}
                    sx={[
                      toolbarIconButtonSx,
                      {
                        width: toolbarControlSize,
                        height: toolbarControlSize,
                        minWidth: toolbarControlSize,
                      },
                    ]}
                  >
                    <Box sx={toolbarIconCircleSx(hoverTarget === 'search')}>
                      <SearchOutlinedIcon
                        sx={{
                          color: toolbarInactiveIconColor,
                          width: '22px !important',
                          height: '22px !important',
                        }}
                      />
                    </Box>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Tooltip
              title="Reset Filters"
              arrow
              enterDelay={toolbarIconTooltipDelayMs}
              slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            >
              <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
              >
                <IconButton
                  size="medium"
                  aria-label="Reset chart filters"
                  onClick={resetToolbarFilters}
                  disabled={!hasActiveToolbarState}
                  sx={[toolbarIconButtonSx, hasActiveToolbarState ? { color: 'primary.main' } : {}]}
                >
                  <Box sx={toolbarIconCircleSx(hoverTarget === 'reset' && hasActiveToolbarState)}>
                    <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
          </Stack>
        ) : headerRightSlot ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCompactHeaderLayout ? 'center' : 'flex-start',
              minWidth: 0,
              flex: isCompactHeaderLayout ? '1 1 auto' : '0 0 auto',
              minHeight: `${headerHeight}px`,
              height: `${headerHeight}px`,
              mr: isCompactHeaderLayout ? 0 : controlsOffsetRight,
            }}
          >
            {headerRightSlot}
          </Box>
        ) : null}
      </Stack>
      <DropdownFiltersPopover
        open={popoverState.type === 'filters'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        heading="Chart Filters"
        headingIcon={
          <FilterListOutlinedIcon sx={{ width: '18px !important', height: '18px !important' }} />
        }
        selectedStatuses={filterStatuses}
        onStatusesChange={(next) => {
          preserveWindowScroll(() => {
            onFilterStatusesChange(next as TStatus[]);
          });
        }}
        statusOptions={statusOptions}
        statusLabel={statusFilterLabel}
        selectedEntities={filterEntities}
        onEntitiesChange={
          onFilterEntitiesChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterEntitiesChange(next);
                });
              }
            : undefined
        }
        entityOptions={entityOptions}
        entityLabel={entityLabel}
        selectedSuites={filterSuites}
        onSuitesChange={
          onFilterSuitesChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterSuitesChange(next);
                });
              }
            : undefined
        }
        suiteOptions={suiteOptions}
        selectedTags={filterTags}
        onTagsChange={(next) => {
          preserveWindowScroll(() => {
            onFilterTagsChange(next);
          });
        }}
        tagOptions={availableTags.map((tag) => ({ value: tag, label: tag }))}
      />
      <TableDatePopover
        open={popoverState.type === 'date'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        dateQuickFilter={dateQuickFilter}
        onDateQuickFilterChange={(next) => {
          preserveWindowScroll(() => {
            onDateQuickFilterChange(next);
          });
        }}
        fromDate={fromDate}
        toDate={toDate}
        previewFromDate={displayedFromDate}
        previewToDate={displayedToDate}
        onFromDateChange={(next) => {
          preserveWindowScroll(() => {
            onFromDateChange(next);
          });
        }}
        onToDateChange={(next) => {
          preserveWindowScroll(() => {
            onToDateChange(next);
          });
        }}
        includeLatestQuickFilter={includeLatestQuickFilter}
      />
    </>
  );
};

type StatusChartPanelProps<TStatus extends string> = ChartPanelToolbarProps<TStatus> & {
  chartHeight?: number;
  width?: number;
  height?: number;
  panelPadding?: ComponentProps<typeof Panel>['padding'];
  panelContentGap?: ComponentProps<typeof Panel>['contentGap'];
  emptyTitle?: string;
  emptyBody?: string;
  children?: ReactNode;
};

type ChartToolbarPanelProps<TStatus extends string> = ChartPanelToolbarProps<TStatus> & {
  width?: number;
  panelPadding?: ComponentProps<typeof Panel>['padding'];
  panelPaddingX?: string | number;
};

export const ChartToolbarPanel = <TStatus extends string>({
  width,
  panelPadding = 'xl',
  panelPaddingX = s('none'),
  ...toolbarProps
}: ChartToolbarPanelProps<TStatus>): ReactElement => (
  <Panel
    padding="none"
    gap="none"
    contentGap="none"
    headerGap="none"
    headerSlot={
      <Box
        sx={{
          minHeight: `${toolbarProps.headerHeight ?? TABLE_PANEL_HEADER_HEIGHT}px`,
        }}
      >
        <ChartPanelToolbar {...toolbarProps} />
      </Box>
    }
    sx={[
      {
        backgroundColor: 'background.paper',
        px: panelPaddingX,
        pt: panelPadding,
        pb: panelPadding,
      },
      width
        ? {
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
          }
        : null,
    ]}
  >
    {null}
  </Panel>
);

export const StatusChartPanel = <TStatus extends string>({
  chartHeight = 400,
  width,
  height,
  panelPadding = 'xl',
  panelContentGap = 'md',
  emptyTitle = 'Status Chart Coming Soon',
  emptyBody = 'Use the toolbar to shape the chart data for the next phase of the UI update.',
  children,
  ...toolbarProps
}: StatusChartPanelProps<TStatus>): ReactElement => (
  <Panel
    padding={panelPadding}
    gap="none"
    contentGap={panelContentGap}
    headerGap="none"
    headerSlot={
      <Box
        sx={{
          minHeight: `${toolbarProps.headerHeight ?? TABLE_PANEL_HEADER_HEIGHT}px`,
        }}
      >
        <ChartPanelToolbar {...toolbarProps} />
      </Box>
    }
    sx={[
      { backgroundColor: 'background.paper' },
      width
        ? {
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
          }
        : null,
      height ? { height: `${height}px` } : null,
    ]}
  >
    {children ?? (
      <Box
        sx={{
          height: `${chartHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ChartEmptyState title={emptyTitle} body={emptyBody} />
      </Box>
    )}
  </Panel>
);
